import { useState, useEffect } from "react";
import axios from 'axios';
import { toast } from 'react-toastify';
import apiService from '../services/apiService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const API = `${BACKEND_URL}/api`;

// Settings Page Component
const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('sip');
  const [sipProfiles, setSipProfiles] = useState([]);
  const [installedTtsModels, setInstalledTtsModels] = useState([]);
  const [aiModels, setAiModels] = useState({ stt: [], llm: [], tts: [] });
  const [loadingModels, setLoadingModels] = useState(false);
  const [providerType, setProviderType] = useState('');
  const [providerData, setProviderData] = useState({});
  const [editingProviderId, setEditingProviderId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [previewAudio, setPreviewAudio] = useState(null);
  const [previewPlaying, setPreviewPlaying] = useState(null);
  const [newSipProfile, setNewSipProfile] = useState({
    name: '',
    host: '',
    username: '',
    password: '',
    port: 5060
  });

  useEffect(() => {
    loadSipProfiles();
    loadInstalledTtsModels();
  }, []);

  useEffect(() => {
    if (activeTab === 'ai' || activeTab === 'tts') {
      loadAiModels();
    }
  }, [activeTab]);

  // Cleanup preview audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudio) {
        previewAudio.pause();
        previewAudio.currentTime = 0;
      }
    };
  }, [previewAudio]);

  const loadSipProfiles = async () => {
    try {
      const response = await axios.get(`${API}/sips/profiles`);
      setSipProfiles(response.data);
    } catch (error) {
      console.error('Error loading SIP profiles:', error);
      // Profil Flashcall par défaut
      setSipProfiles([
        {
          id: 'flashcall',
          name: 'Flashcall',
          host: '217.69.5.180',
          port: 5060,
          enabled: true
        }
      ]);
    }
  };

  const loadInstalledTtsModels = async () => {
    try {
      const response = await axios.get(`${API}/tts-models/installed`);
      setInstalledTtsModels(response.data);
    } catch (error) {
      console.error('Error loading TTS models:', error);
      // Modèles par défaut
      setInstalledTtsModels([]);
    }
  };

  const loadAiModels = async () => {
    setLoadingModels(true);
    try {
      const token = localStorage.getItem('voxipro_token');
      const response = await axios.get(`${API}/models/available`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.data && response.data.models) {
        setAiModels({
          stt: response.data.models.stt || [],
          llm: response.data.models.llm || [],
          tts: response.data.models.tts || []
        });
      }
    } catch (error) {
      console.error('Error loading AI models:', error);
      setAiModels({ stt: [], llm: [], tts: [] });
    } finally {
      setLoadingModels(false);
    }
  };

  // Helper functions for model data with fallbacks
  const getModelName = (model, modelType) => {
    let name = model?.model || model?.name || 'N/A';
    // Format Whisper model names: if it's just "tiny", show as "Whisper(tiny)" with language code
    if (modelType === 'stt' && name.toLowerCase() === 'tiny') {
      name = 'Whisper(tiny)';
      // Append language code if available
      if (model?.language === 'en') {
        name = 'Whisper(tiny) EN';
      } else if (model?.language === 'fr') {
        name = 'Whisper(tiny) FR';
      }
    }
    return name;
  };

  const getModelSize = (model) => {
    return model?.model_size || 'Not available';
  };

  const getModelDescription = (model) => {
    return model?.description || 'Not available';
  };

  const getModelStatus = (model) => {
    const status = model?.status;
    if (status === 'running' || status === 'available') {
      return { text: 'Available', color: 'green', icon: '✓' };
    }
    return { text: 'Unavailable', color: 'red', icon: '✗' };
  };

  const calculateTotalSize = () => {
    const allModels = [...aiModels.stt, ...aiModels.llm, ...aiModels.tts];
    const sizes = allModels
      .map(model => model?.model_size)
      .filter(size => size && size !== 'Not available' && typeof size === 'string');
    
    if (sizes.length === 0) return 'Not available';
    
    // Try to parse and sum sizes (simplified - would need proper parsing for different units)
    // For now, just return a placeholder
    return '~3.2GB';
  };

  const getActiveModelsCount = () => {
    const allModels = [...aiModels.stt, ...aiModels.llm, ...aiModels.tts];
    return allModels.filter(model => {
      const status = model?.status;
      return status === 'running' || status === 'available';
    }).length;
  };

  const editSipProvider = (provider) => {
    // Préremplir le formulaire avec les données du fournisseur
    const providerId = provider.id || provider._id;
    setEditingProviderId(providerId);
    setProviderType('sip');
    setProviderData({
      name: provider.name,
      host: provider.host,
      port: provider.port,
      username: provider.username || '',
      password: provider.password || '',
      transport: provider.configuration?.transport || 'UDP',
      codec: provider.configuration?.codec || 'PCMA',
      cost_per_minute: provider.cost_per_minute || 0
    });
    setActiveTab('add-provider');
  };

  const handleDeleteClick = (providerId) => {
    const id = typeof providerId === 'object' ? (providerId.id || providerId._id) : providerId;
    const provider = sipProfiles.find(p => (p.id || p._id) === id);
    setProviderToDelete({ id, name: provider?.name || 'ce fournisseur' });
    setShowDeleteModal(true);
  };

  const confirmDeleteSipProvider = async () => {
    if (!providerToDelete) return;
    
    try {
      const id = providerToDelete.id;
      if (!id) {
        toast.error('ID du fournisseur introuvable');
        setShowDeleteModal(false);
        setProviderToDelete(null);
        return;
      }
      const response = await axios.delete(`${API}/sips/${id}`);
      if (response.data.message || response.data.success) {
        toast.success('Fournisseur SIP supprimé avec succès !');
        loadSipProfiles(); // Recharger la liste
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting SIP provider:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la suppression du fournisseur';
      toast.error(errorMessage);
    } finally {
      setShowDeleteModal(false);
      setProviderToDelete(null);
    }
  };

  const addSipProfile = async () => {
    try {
      const response = await axios.post(`${API}/sips`, newSipProfile);
      setSipProfiles([...sipProfiles, response.data]);
      setNewSipProfile({ name: '', host: '', username: '', password: '', port: 5060 });
      toast.success('Profil SIP ajouté avec succès !');
      loadSipProfiles(); // Reload the list
    } catch (error) {
      console.error('Error adding SIP profile:', error);
      toast.error('Erreur lors de l\'ajout du profil SIP');
    }
  };

  const addProvider = async () => {
    try {
      if (providerType === 'sip') {
        // Comprehensive validation for all fields
        if (!providerData.name || providerData.name.trim() === '') {
          toast.error('Le nom du fournisseur est requis');
          return;
        }
        if (!providerData.username || providerData.username.trim() === '') {
          toast.error('Le nom d\'utilisateur est requis');
          return;
        }
        if (!providerData.password || providerData.password.trim() === '') {
          toast.error('Le mot de passe est requis');
          return;
        }
        if (!providerData.host || providerData.host.trim() === '') {
          toast.error('L\'hôte est requis');
          return;
        }
        // if (!providerData.port || providerData.port === '') {
        //   toast.error('Le port est requis');
        //   return;
        // }
        // const portNum = parseInt(providerData.port);
        // if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
        //   toast.error('Le port doit être un nombre entre 1 et 65535');
        //   return;
        // }
        // if (!providerData.transport || providerData.transport === '') {
        //   toast.error('Le transport est requis');
        //   return;
        // }
        // if (!providerData.codec || providerData.codec === '') {
        //   toast.error('Le codec est requis');
        //   return;
        // }
        if (!providerData.cost_per_minute || providerData.cost_per_minute === '') {
          toast.error('Le coût par minute est requis');
          return;
        }
        const costNum = parseFloat(providerData.cost_per_minute);
        if (isNaN(costNum) || costNum < 0) {
          toast.error('Le coût par minute doit être un nombre valide supérieur ou égal à 0');
          return;
        }

        // Création ou mise à jour spécifique pour SIP
        const sipProfile = {
          name: providerData.name.trim(),
          provider: 'custom', // Required field by SIP model
          host: providerData.host.trim(),
          // port: portNum,
          username: providerData.username.trim(),
          password: providerData.password,
          enabled: true,
          status: 'inactive',
          cost_per_minute: costNum,
          configuration: {
            transport: providerData.transport,
            codec: providerData.codec
          }
        };

        let response;
        if (editingProviderId) {
          // Mise à jour
          response = await axios.put(`${API}/sips/${editingProviderId}`, sipProfile);
          if (response.data.id || response.data._id) {
            toast.success('Fournisseur SIP modifié avec succès !');
          } else {
            toast.error('Erreur lors de la modification du fournisseur SIP');
            return;
          }
        } else {
          // Création
          response = await axios.post(`${API}/sips`, sipProfile);
          if (response.data.id || response.data._id) {
            toast.success('Fournisseur SIP créé avec succès !');
          } else {
            toast.error('Erreur lors de la création du fournisseur SIP');
            return;
          }
        }

        // Réinitialiser le formulaire
        setProviderType('');
        setProviderData({});
        setEditingProviderId(null);
        loadSipProfiles(); // Reload the list
        setActiveTab('providers');
      } else {
        // Autres types de fournisseurs - endpoint may not exist yet
        toast.warning(`L'ajout de fournisseurs ${providerType.toUpperCase()} n'est pas encore implémenté`);
        console.warn(`Provider type ${providerType} not yet supported`);
      }
    } catch (error) {
      console.error('Error adding/updating provider:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || 'Erreur lors de l\'opération';
      toast.error(errorMessage);
    }
  };

  const renderProviderFields = () => {
    switch (providerType) {
      case 'sip':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du Fournisseur <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={providerData.name || ''}
                onChange={(e) => setProviderData({...providerData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Flashcall"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom d'utilisateur <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={providerData.username || ''}
                onChange={(e) => setProviderData({...providerData, username: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="KC_TEST_IP"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de Passe <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                required
                value={providerData.password || ''}
                onChange={(e) => setProviderData({...providerData, password: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="RpMtPc9dFY8VshyU"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hôte <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={providerData.host || ''}
                onChange={(e) => setProviderData({...providerData, host: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="217.69.5.180"
              />
            </div>
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Port <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                required
                min="1"
                max="65535"
                value={providerData.port || 5060}
                onChange={(e) => setProviderData({...providerData, port: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="5060"
              />
            </div> */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transport <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={providerData.transport || 'UDP'}
                onChange={(e) => setProviderData({...providerData, transport: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sélectionnez un transport</option>
                <option value="UDP">UDP</option>
                <option value="TCP">TCP</option>
                <option value="TLS">TLS</option>
              </select>
            </div> */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Codec <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={providerData.codec || 'PCMA'}
                onChange={(e) => setProviderData({...providerData, codec: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sélectionnez un codec</option>
                <option value="PCMA">PCMA</option>
                <option value="PCMU">PCMU</option>
                <option value="G729">G729</option>
              </select>
            </div> */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coût par minute (€) <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={providerData.cost_per_minute || ''}
                onChange={(e) => setProviderData({...providerData, cost_per_minute: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="0.12"
              />
              <p className="mt-1 text-xs text-gray-500">
                Coût par minute de communication téléphonique pour ce fournisseur SIP
              </p>
            </div>
          </div>
        );
      case 'stt':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du Fournisseur
              </label>
              <input
                type="text"
                value={providerData.name || ''}
                onChange={(e) => setProviderData({...providerData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Whisper OpenAI"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clé API
              </label>
              <input
                type="password"
                value={providerData.api_key || ''}
                onChange={(e) => setProviderData({...providerData, api_key: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="sk-..."
              />
            </div>
          </div>
        );
      case 'llm':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du Fournisseur
              </label>
              <input
                type="text"
                value={providerData.name || ''}
                onChange={(e) => setProviderData({...providerData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="GPT-4 OpenAI"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clé API
              </label>
              <input
                type="password"
                value={providerData.api_key || ''}
                onChange={(e) => setProviderData({...providerData, api_key: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="sk-..."
              />
            </div>
          </div>
        );
      case 'tts':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du Fournisseur
              </label>
              <input
                type="text"
                value={providerData.name || ''}
                onChange={(e) => setProviderData({...providerData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="ElevenLabs"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clé API
              </label>
              <input
                type="password"
                value={providerData.api_key || ''}
                onChange={(e) => setProviderData({...providerData, api_key: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="sk_c4fbaad359e904c8ee03951d1d300ebb0a760dab0d79fe7b"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de base API
              </label>
              <input
                type="url"
                value={providerData.base_url || ''}
                onChange={(e) => setProviderData({...providerData, base_url: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://api.elevenlabs.io/v1/text-to-speech/rachel"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ID Voix TTS
              </label>
              <input
                type="text"
                value={providerData.voice_id || ''}
                onChange={(e) => setProviderData({...providerData, voice_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="elevenlabs.rachel"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Langue TTS
              </label>
              <select
                value={providerData.language || 'US'}
                onChange={(e) => setProviderData({...providerData, language: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="US">US</option>
                <option value="FR">FR</option>
                <option value="EN">EN</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Genre Voix TTS
              </label>
              <select
                value={providerData.gender || 'female'}
                onChange={(e) => setProviderData({...providerData, gender: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Voix Spéciale (Marie)
              </label>
              <input
                type="text"
                value={providerData.special_voice || ''}
                onChange={(e) => setProviderData({...providerData, special_voice: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="sANWqF1bCMzR6eyZbCGw"
              />
            </div>
          </div>
        );
      default:
        return (
          <div className="text-center text-gray-500 py-8">
            Sélectionnez un type de fournisseur pour voir les champs de configuration
          </div>
        );
    }
  };

  const testSipConnection = async (profileId) => {
    try {
      toast.info('🔍 Test de connexion SIP...');
      const id = typeof profileId === 'object' ? (profileId.id || profileId._id) : profileId;
      const response = await axios.post(`${API}/sips/${id}/test`);
      if (response.data.success) {
        toast.success('✅ Connexion SIP réussie !');
      } else {
        toast.error('❌ Erreur de connexion SIP');
      }
    } catch (error) {
      console.error('Error testing SIP:', error);
      toast.error('Erreur lors du test SIP - L\'endpoint de test n\'est peut-être pas encore implémenté');
    }
  };

  const testTtsModel = async (model, index) => {
    // Stop any currently playing preview
    if (previewAudio) {
      previewAudio.pause();
      previewAudio.currentTime = 0;
      setPreviewAudio(null);
    }

    // Check if model is available
    const status = getModelStatus(model);
    if (status.color !== 'green') {
      toast.error('Le modèle n\'est pas disponible pour le test');
      return;
    }

    // Get model identifier
    const modelId = model.id || model._id;
    if (!modelId) {
      toast.error('ID du modèle introuvable');
      return;
    }

    try {
      const modelKey = modelId || index;
      setPreviewPlaying(modelKey);
      toast.info('🔊 Génération de l\'audio en cours...');

      // Fetch voices for this TTS model
      let voices = [];
      try {
        const voicesResponse = await apiService.voices.getByProvider(modelId);
        voices = voicesResponse.data || [];
      } catch (error) {
        console.error('Error fetching voices:', error);
        toast.error('Erreur lors de la récupération des voix pour ce modèle');
        setPreviewPlaying(null);
        return;
      }

      if (!voices || voices.length === 0) {
        toast.error('Aucune voix disponible pour ce modèle TTS');
        setPreviewPlaying(null);
        return;
      }

      // Get the first available voice
      const firstVoice = voices[0];
      const voiceId = firstVoice.id;

      // Determine test text based on model language
      const modelLanguage = model.language || '';
      const isFrench = modelLanguage.toLowerCase() === 'fr' || modelLanguage.toLowerCase() === 'fr-fr';
      const testText = isFrench 
        ? 'Bonjour, ceci est un test du modèle TTS.'
        : 'Hello, this is a test of the TTS model.';

      // Generate TTS
      const response = await apiService.tts.generate({
        text: testText,
        voice: voiceId,
        format: 'wav',
        use_case: 'voice_test'
      });

      if (response.data.success) {
        // Create audio element to play TTS
        const audio = new Audio();
        // Construct full URL if audio_url is relative
        const audioUrl = response.data.audio_url.startsWith('http') 
          ? response.data.audio_url 
          : `${BACKEND_URL}${response.data.audio_url}`;
        audio.src = audioUrl;
        setPreviewAudio(audio);

        audio.onloadeddata = () => {
          toast.success('🎵 Audio généré avec succès !');
          audio.play();
        };

        audio.onerror = () => {
          toast.error('Erreur lors de la lecture audio');
          setPreviewPlaying(null);
          setPreviewAudio(null);
        };

        audio.onended = () => {
          setPreviewPlaying(null);
          setPreviewAudio(null);
        };
      } else {
        toast.error(response.data.message || 'Erreur lors de la génération TTS');
        setPreviewPlaying(null);
      }
    } catch (error) {
      console.error('Error testing TTS model:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Erreur lors du test du modèle TTS';
      toast.error(errorMessage);
      setPreviewPlaying(null);
      setPreviewAudio(null);
    }
  };

  const tabs = [
    { id: 'sip', label: 'Configuration SIP', icon: '📞' },
    { id: 'ai', label: 'Modèles IA', icon: '🤖' },
    { id: 'tts', label: 'Modèles TTS', icon: '🔊' },
    { id: 'providers', label: 'Fournisseurs', icon: '⚙️' }
  ];

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Paramètres Avancés</h1>
        
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
        
        {/* SIP Configuration */}
        {activeTab === 'sip' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">Configuration SIP</h2>
            
            <div className="space-y-6">
              {sipProfiles.map((profile) => (
                <div key={profile.id || profile._id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium">{profile.name}</h3>
                      <p className="text-sm text-gray-600">{profile.host}:{profile.port}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        profile.status === 'connected' || profile.enabled || profile.status === 'active'
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(profile.status === 'connected' || profile.enabled || profile.status === 'active') ? 'active' : 'inactive'}
                      </span>
                      <button
                        onClick={() => testSipConnection(profile.id)}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                      >
                        Test
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* STT Configuration */}
        {activeTab === 'stt' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">Configuration STT (Speech-to-Text)</h2>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">WhisperX Large-V3</h3>
                    <p className="text-sm text-gray-600">Modèle haute précision pour le français</p>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Actif
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* LLM Configuration */}
        {activeTab === 'llm' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">Configuration LLM (Langage)</h2>
            <div className="space-y-4">
              <div className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Mistral 7B</h3>
                    <p className="text-sm text-gray-600">Modèle conversationnel français</p>
                  </div>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Actif
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* AI Models Configuration */}
        {activeTab === 'ai' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">Lightweight AI Models - Development Environment</h2>
            {loadingModels ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading models...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* STT Models */}
                <div className="border-l-4 border-blue-500 pl-4">
                  <h3 className="font-semibold text-blue-900 mb-3 text-lg">🎤 Speech-to-Text (STT)</h3>
                  <div className="space-y-3">
                    {aiModels.stt.length > 0 ? (
                      aiModels.stt.map((model, index) => {
                        const status = getModelStatus(model);
                        return (
                          <div key={model.id || index} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-blue-900 mb-2">{getModelName(model, 'stt')}</h4>
                                <p className="text-sm text-gray-700 mb-1">{getModelDescription(model)}</p>
                              </div>
                              <div className="ml-4 text-right flex flex-col items-end">
                                <div className={`flex items-center gap-1 mb-2 ${status.color === 'green' ? 'text-green-600' : 'text-red-600'}`}>
                                  <span className="text-lg font-bold">{status.icon}</span>
                                  <span className="text-sm font-medium">{status.text}</span>
                                </div>
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">Size:</span> {getModelSize(model)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-gray-600 text-sm">No STT models available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* LLM Models */}
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="font-semibold text-purple-900 mb-3 text-lg">🧠 Large Language Model (LLM)</h3>
                  <div className="space-y-3">
                    {aiModels.llm.length > 0 ? (
                      aiModels.llm.map((model, index) => {
                        const status = getModelStatus(model);
                        return (
                          <div key={model.id || index} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-purple-900 mb-2">{getModelName(model, 'llm')}</h4>
                                <p className="text-sm text-gray-700 mb-1">{getModelDescription(model)}</p>
                              </div>
                              <div className="ml-4 text-right flex flex-col items-end">
                                <div className={`flex items-center gap-1 mb-2 ${status.color === 'green' ? 'text-green-600' : 'text-red-600'}`}>
                                  <span className="text-lg font-bold">{status.icon}</span>
                                  <span className="text-sm font-medium">{status.text}</span>
                                </div>
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">Size:</span> {getModelSize(model)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <p className="text-gray-600 text-sm">No LLM models available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* TTS Models */}
                <div className="border-l-4 border-green-500 pl-4">
                  <h3 className="font-semibold text-green-900 mb-3 text-lg">🔊 Text-to-Speech (TTS)</h3>
                  <div className="space-y-3">
                    {aiModels.tts.length > 0 ? (
                      aiModels.tts.map((model, index) => {
                        const status = getModelStatus(model);
                        return (
                          <div key={model.id || index} className="p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-green-900 mb-2">{getModelName(model, 'tts')}</h4>
                                <p className="text-sm text-gray-700 mb-1">{getModelDescription(model)}</p>
                              </div>
                              <div className="ml-4 text-right flex flex-col items-end">
                                <div className={`flex items-center gap-1 mb-2 ${status.color === 'green' ? 'text-green-600' : 'text-red-600'}`}>
                                  <span className="text-lg font-bold">{status.icon}</span>
                                  <span className="text-sm font-medium">{status.text}</span>
                                </div>
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">Size:</span> {getModelSize(model)}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-gray-600 text-sm">No TTS models available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Configuration Summary */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Configuration Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Total Size</p>
                      <p className="font-semibold text-gray-900">{calculateTotalSize()}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Active Models</p>
                      <p className="font-semibold text-gray-900 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        {getActiveModelsCount()} active models
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Type</p>
                      <p className="font-semibold text-gray-900 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        100% Lightweight
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Mode</p>
                      <p className="font-semibold text-gray-900 flex items-center gap-2">
                        <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                        Development Mode
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* TTS Configuration */}
        {activeTab === 'tts' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-6">TTS (Text-to-Speech) setup</h2>
            {loadingModels ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading models...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Display AI TTS Models from endpoint */}
                {aiModels.tts.length > 0 && (
                  <>
                    {aiModels.tts.map((model, index) => {
                      const status = getModelStatus(model);
                      const modelKey = model.id || index;
                      const isAvailable = status.color === 'green';
                      const isPlaying = previewPlaying === modelKey;
                      return (
                        <div key={model.id || index} className="border rounded-lg p-4 flex items-center justify-between bg-green-50 border-green-200">
                          <div className="flex-1">
                            <h3 className="font-medium text-green-900">{getModelName(model, 'tts')}</h3>
                            <p className="text-sm text-gray-600 mb-1">{getModelDescription(model)}</p>
                            <p className="text-xs text-gray-500">Size: {getModelSize(model)}</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 ${
                              status.color === 'green'
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              <span>{status.icon}</span>
                              {status.text}
                            </span>
                            <button
                              onClick={() => testTtsModel(model, index)}
                              disabled={!isAvailable || isPlaying}
                              className={`px-3 py-1 rounded text-sm transition-colors ${
                                isAvailable && !isPlaying
                                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                                  : 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-50'
                              }`}
                            >
                              {isPlaying ? '⏸️ Génération...' : '🔊 Test'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
                {/* Display installed TTS models (if any) */}
                {installedTtsModels.map((model) => {
                  const isActive = model.status === 'active';
                  return (
                    <div key={model.id} className="border rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{model.name}</h3>
                        <p className="text-sm text-gray-600">{model.language}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          isActive
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {model.status}
                        </span>
                        <button
                          onClick={() => toast.info(`Test du modèle ${model.name}`)}
                          disabled={!isActive}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            isActive
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-50'
                          }`}
                        >
                          🔊 Test
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Show message if no models available */}
                {aiModels.tts.length === 0 && installedTtsModels.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No TTS models available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Liste des fournisseurs SIP dynamique */}
        {activeTab === 'providers' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Fournisseurs Configurés</h2>
              <button
                onClick={() => setActiveTab('add-provider')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                + Ajouter un fournisseur
              </button>
            </div>
            <div className="space-y-4">
              {sipProfiles.map((provider) => (
                <div key={provider.id || provider._id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">{provider.name}</h3>
                      <p className="text-sm text-gray-600">{provider.host}:{provider.port}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        provider.enabled 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {provider.enabled ? 'Activé' : 'Désactivé'}
                      </span>
                      <button 
                        onClick={() => editSipProvider(provider)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Modifier
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(provider.id || provider._id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Provider Form */}
        {activeTab === 'add-provider' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {editingProviderId ? 'Modifier le Fournisseur' : 'Ajouter un Nouveau Fournisseur'}
              </h2>
              <button
                onClick={() => {
                  setActiveTab('providers');
                  setEditingProviderId(null);
                  setProviderType('');
                  setProviderData({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ← Retour
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de Fournisseur
                </label>
                <select 
                  value={providerType}
                  onChange={(e) => {
                    setProviderType(e.target.value);
                    setProviderData({});
                    setEditingProviderId(null); // Reset editing when changing type
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!!editingProviderId} // Disable type change when editing
                >
                  <option value="">Sélectionnez un type</option>
                  <option value="sip">Fournisseur SIP</option>
                  <option value="tts">Modèle TTS</option>
                  <option value="stt">Modèle STT</option>
                  <option value="llm">Modèle LLM</option>
                </select>
              </div>
              
              {renderProviderFields()}
              
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setActiveTab('providers');
                    setEditingProviderId(null);
                    setProviderType('');
                    setProviderData({});
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={addProvider}
                  disabled={!providerType}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {editingProviderId ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Confirmer la suppression
                </h3>
                <p className="text-gray-700 mb-6">
                  Êtes-vous sûr de vouloir supprimer le fournisseur SIP <span className="font-semibold">"{providerToDelete?.name}"</span> ?
                  Cette action est irréversible.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setProviderToDelete(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={confirmDeleteSipProvider}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage